import { useManager } from 'dreamstate';
import { ChangeEvent, FormEvent, useCallback, useRef, useState } from 'react';

import {
  rem,
  Flex,
  Box,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  IconButton,
  Switch,
} from '@vimeo/bokeh';
import { Send } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { ISelectedEmoji } from '../../../../components/emoji';
import { browserConfig } from '../../../../configs/BrowserConfig';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '../../../../hooks/live/useActionConfirmation';
import { IQnAContext, QnAManager } from '../../../../store/firebase/interaction/qna';
import { trackAskQuestion } from '../../../../tracking/tracking_engagement_qna';
import {
  T_ANONYMOUS,
  T_ASK_QUESTION,
  T_CHARACTERS,
  T_QUESTION_SUBMITTED,
} from '../../../../translations';
import { stopEventPropagation } from '../../../../utils/dom/event';
import { createDomName } from '../../../../utils/dom/identifying';
import { FAILED_STATUS_COLOR } from '../../../../utils/styling/colors';
import { EmojiButton } from '../../shared/EmojiButton';

interface IQnaInputFormProps extends IBaseDomElementProps {
  isDisabled?: boolean;
  qnaContext?: IQnAContext;
}

export function QnaInputForm({
  id = createDomName('qna-question-input-form'),
  className = createDomName('qna-question-input-form'),
  isDisabled = false,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  qnaContext: { isAnonymousQuestionsDisabled, qnaActions } = useManager(
    QnAManager,
    ({ isAnonymousQuestionsDisabled }) => [isAnonymousQuestionsDisabled],
  ),
}: IQnaInputFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState<string>('');
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [submitTimeout, setSubmitTimeout] = useState<boolean>(false);

  const notify: TNotificationHandler = useSimpleNotification();

  const onQuestionEdited = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value);
  }, []);

  const onAnonymousToggle = useCallback(() => {
    setAnonymous((it) => !it);
    inputRef.current?.focus();
  }, [setAnonymous]);

  const onSubmitQuestion = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!question.length || question.length > interactionToolsConfig.QNA.MAX_QUESTION_LENGTH) {
        return;
      }

      if (submitTimeout) {
        return;
      } else {
        setSubmitTimeout(true);
      }

      try {
        await qnaActions.submitQuestion(question, anonymous);

        setQuestion('');

        trackAskQuestion();
        notify({ message: T_QUESTION_SUBMITTED });

        setTimeout(
          () => setSubmitTimeout(false),
          interactionToolsConfig.QNA.QUESTION_ASKING_THROTTLE,
        );
      } catch (error) {
        setSubmitTimeout(false);
      }
    },
    [question, submitTimeout, qnaActions, anonymous, notify],
  );

  const onEmojiSelect = useCallback((selection: ISelectedEmoji) => {
    if (inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart || 0;
      const value = inputRef.current.value;
      const newValue =
        value.slice(0, cursorPosition) +
        selection.native +
        value.slice(cursorPosition, value.length);
      setQuestion(newValue);
      inputRef.current.focus();
    }
  }, []);

  const canInput: boolean = !isDisabled;
  const canSubmit: boolean = Boolean(
    canInput &&
      question.length &&
      question.length <= interactionToolsConfig.QNA.MAX_QUESTION_LENGTH &&
      !submitTimeout,
  );
  const charsLeft: number = interactionToolsConfig.QNA.MAX_QUESTION_LENGTH - question.length;
  const isCharsNearLimit: boolean = charsLeft <= 20;
  const isCharsOverLimit: boolean = charsLeft < 0;

  return (
    <Box
      id={id}
      className={className}
      as={'form'}
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      paddingTop={rem(8)}
      onSubmit={onSubmitQuestion}
      onKeyUp={stopEventPropagation}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
    >
      <Flex
        id={createDomName(id, 'title')}
        className={createDomName(className, 'title')}
        justifyContent={'space-between'}
        alignItems={'center'}
        marginBottom={rem(12)}
        color={'text-primary'}
        fontSize={'text-xs'}
        fontWeight={'bold'}
      >
        <div>{T_ASK_QUESTION}</div>

        {isAnonymousQuestionsDisabled ? null : (
          <Flex alignItems={'center'} gap={rem(8)}>
            <Flex fontSize={'text-xs'} fontWeight={500}>
              {T_ANONYMOUS}
            </Flex>

            <Switch
              id={createDomName(id, 'anonymous-toggle')}
              className={createDomName(className, 'anonymous-toggle')}
              size={'sm'}
              isChecked={anonymous}
              onChange={onAnonymousToggle}
            />
          </Flex>
        )}
      </Flex>

      <InputGroup
        id={createDomName(id, 'controls')}
        className={createDomName(className, 'controls')}
      >
        {browserConfig.BROWSER?.isMobile ? null : (
          <InputLeftElement>
            <EmojiButton
              id={createDomName(id, 'emoji-button')}
              className={createDomName(className, 'emoji-button')}
              inputRef={inputRef}
              isSubmitted={submitTimeout}
              placement={'top-start'}
              isDisabled={!canInput}
              onEmojiSelect={onEmojiSelect}
            />
          </InputLeftElement>
        )}

        <Input
          id={createDomName(id, 'input')}
          className={createDomName(className, 'input')}
          ref={inputRef}
          maxLength={interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH}
          value={question}
          autoComplete={'off'}
          isDisabled={!canInput}
          size={'md'}
          onChange={onQuestionEdited}
        />

        <InputRightElement>
          <IconButton
            id={createDomName(id, 'send-button')}
            className={createDomName(className, 'send-button')}
            aria-label={'qna-submit-button'}
            data-chat-submit
            type={'submit'}
            size={'sm'}
            variant={'minimal'}
            icon={<Send />}
            isDisabled={!canSubmit}
            transition={'none'}
            _hover={{
              backgroundColor: 'transparent!important',
            }}
          />
        </InputRightElement>
      </InputGroup>

      <Flex
        id={createDomName(id, 'info')}
        className={createDomName(className, 'info')}
        justifyContent={'space-between'}
        alignContent={'center'}
        width={'100%'}
      >
        <Flex
          id={createDomName(id, 'characters-count')}
          className={createDomName(className, 'characters-count')}
          as={'span'}
          alignItems={'center'}
          fontSize={'text-xs'}
          lineHeight={'text-xs'}
          padding={`${rem(8)} 0 0`}
          fontWeight={isCharsNearLimit || isCharsOverLimit ? 'bold' : 400}
          color={isCharsOverLimit ? FAILED_STATUS_COLOR : 'text-secondary'}
        >
          {charsLeft} {T_CHARACTERS}
        </Flex>
      </Flex>
    </Box>
  );
}
